import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Beaker, CalendarPlus, Check, ChevronLeft, KeyRound, MapPin, Plus, ShieldOff, UserX, XCircle } from "lucide-react";
import { getUser, resetPassword, deleteUser, restoreUser, setSandbox } from "../api/users";
import { userCoursesProgress, type MyCourseSummary } from "../api/assignments";
import {
  completeObservation,
  createObservation,
  listForUser,
  type FieldObservationOut,
} from "../api/fieldPractice";
import {
  cancelExam,
  completeExam,
  listExamsForUser,
  type ExamScheduleOut,
} from "../api/examSchedules";
import { ScheduleExamDialog } from "../components/ScheduleExamDialog";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ROLE_LABELS, type AcademyRole, type UserDetail } from "../types/user";
import { PasswordReveal } from "../components/PasswordReveal";
import {
  formatMoscowDate,
  formatMoscowDateTime,
  moscowDateTimeToUtcIso,
} from "../utils/datetime";

const STAFF_ROLES: AcademyRole[] = [
  "super_admin",
  "admin",
  "content_manager",
  "curator",
  "mentor",
];

type TabKey =
  | "overview"
  | "courses"
  | "tests"
  | "practice"
  | "exams"
  | "speech"
  | "comments";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Обзор" },
  { key: "courses", label: "Курсы" },
  { key: "tests", label: "Тесты" },
  { key: "practice", label: "Практика" },
  { key: "exams", label: "Экзамены" },
  { key: "speech", label: "Спич" },
  { key: "comments", label: "Комментарии" },
];

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const id = Number(userId);
  const { user: me } = useCurrentUser();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [courses, setCourses] = useState<MyCourseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("overview");
  const [resetPwd, setResetPwd] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setError("Некорректный ID");
      return;
    }
    void Promise.all([
      getUser(id).then(setUser),
      userCoursesProgress(id).then((r) => setCourses(r.items)).catch(() => setCourses([])),
    ]).catch((err) => {
      if (err instanceof ApiError && err.status === 404) setError("Пользователь не найден");
      else if (err instanceof ApiError && err.status === 403) setError("Нет доступа");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    });
  }, [id]);

  const stats = useMemo(() => {
    const list = courses ?? [];
    const lessonsTotal = list.reduce((s, c) => s + c.lessons_total, 0);
    const lessonsDone = list.reduce((s, c) => s + c.lessons_completed, 0);
    const coursesDone = list.filter((c) => c.percent === 100).length;
    const percent = lessonsTotal === 0 ? 0 : Math.round((lessonsDone / lessonsTotal) * 100);
    return {
      lessonsTotal,
      lessonsDone,
      coursesTotal: list.length,
      coursesDone,
      percent,
    };
  }, [courses]);

  const canManage = me?.roles.some((r) => STAFF_ROLES.includes(r)) ?? false;

  if (!me) return null;

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-12 text-center text-ink-muted">
        {error}
        <div className="mt-4">
          <Link to="/users" className="text-sm text-brand hover:underline">
            ← к списку учеников
          </Link>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="h-8 w-48 animate-pulse rounded bg-surface-alt" />
        <div className="mt-6 h-32 animate-pulse rounded-xl bg-surface-alt" />
      </div>
    );
  }

  const isDeleted = !!user.deleted_at;
  const roleLabel = user.roles
    .map((r) => ROLE_LABELS[r] ?? r)
    .join(", ");

  async function handleResetPassword() {
    if (!user) return;
    if (!confirm(`Сбросить пароль ${user.login}?`)) return;
    setBusy(true);
    try {
      const r = await resetPassword(user.id);
      setResetPwd(r.generated_password);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  async function handleSoftDelete() {
    if (!user) return;
    if (!confirm(`Деактивировать ${user.login}?`)) return;
    setBusy(true);
    try {
      await deleteUser(user.id);
      const updated = await getUser(user.id);
      setUser(updated);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleSandbox() {
    if (!user) return;
    const next = !user.is_sandbox;
    if (
      !confirm(
        next
          ? `Перевести ${user.login} в режим песочницы? Аккаунт будет помечен как тестовый.`
          : `Снять режим песочницы с ${user.login}?`,
      )
    )
      return;
    setBusy(true);
    try {
      const updated = await setSandbox(user.id, next);
      setUser(updated);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  async function handleRestore() {
    if (!user) return;
    setBusy(true);
    try {
      await restoreUser(user.id);
      const updated = await getUser(user.id);
      setUser(updated);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <div className="mb-3">
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ChevronLeft size={14} strokeWidth={2} />
          К списку учеников
        </Link>
      </div>

      {/* Header */}
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-ink">{user.full_name}</h1>
            <span className="badge badge-brand">{roleLabel}</span>
            {user.is_sandbox && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-ochre/15 px-2 py-0.5 text-xs font-medium text-brand-ochre">
                <Beaker size={11} strokeWidth={2.25} />
                Песочница
              </span>
            )}
            {isDeleted && <span className="badge badge-danger">Деактивирован</span>}
          </div>
          <div className="mt-1 text-sm text-ink-muted">
            <span className="font-mono">{user.login}</span>
            {user.phone && <span> · {user.phone}</span>}
          </div>
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={busy || isDeleted}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <KeyRound size={14} strokeWidth={2} />
              Сбросить пароль
            </button>
            <button
              type="button"
              onClick={handleToggleSandbox}
              disabled={busy || isDeleted}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                user.is_sandbox
                  ? "bg-brand-ochre/15 text-brand-ochre hover:bg-brand-ochre/25"
                  : "border border-line bg-white text-ink hover:bg-surface-alt"
              }`}
              title={
                user.is_sandbox
                  ? "Снять режим песочницы"
                  : "Перевести в режим песочницы"
              }
            >
              <Beaker size={14} strokeWidth={2} />
              {user.is_sandbox ? "Песочница вкл." : "В песочницу"}
            </button>
            {isDeleted ? (
              <button
                type="button"
                onClick={handleRestore}
                disabled={busy}
                className="btn-secondary"
              >
                Восстановить
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSoftDelete}
                disabled={busy}
                className="btn-danger inline-flex items-center gap-1.5"
              >
                <UserX size={14} strokeWidth={2} />
                Деактивировать
              </button>
            )}
          </div>
        )}
      </header>

      {resetPwd && (
        <div className="card mb-4 border-l-4 border-brand-ochre p-4">
          <div className="text-sm font-semibold text-ink">Новый пароль</div>
          <p className="mt-1 text-xs text-ink-muted">
            Передайте ученику. После показа сохранить будет нельзя.
          </p>
          <div className="mt-2">
            <PasswordReveal password={resetPwd} onClose={() => setResetPwd(null)} />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-5 border-b border-line">
        <nav className="flex flex-wrap gap-1">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative -mb-px px-4 py-2.5 text-sm font-medium transition ${
                  active
                    ? "border-b-2 border-brand text-brand"
                    : "border-b-2 border-transparent text-ink-soft hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === "overview" && (
        <OverviewTab user={user} stats={stats} courses={courses} />
      )}
      {tab === "courses" && <CoursesTab courses={courses} />}
      {tab === "practice" && <PracticeTab userId={user.id} canManage={canManage} />}
      {tab === "exams" && (
        <ExamsTab
          userId={user.id}
          userName={user.full_name}
          canManage={canManage}
        />
      )}
      {tab !== "overview" &&
        tab !== "courses" &&
        tab !== "practice" &&
        tab !== "exams" && <ComingSoonTab />}
    </div>
  );
}

function OverviewTab({
  user: _user,
  stats,
  courses,
}: {
  user: UserDetail;
  stats: {
    lessonsTotal: number;
    lessonsDone: number;
    coursesTotal: number;
    coursesDone: number;
    percent: number;
  };
  courses: MyCourseSummary[] | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Progress donut */}
      <section className="card p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Общий прогресс
        </div>
        <div className="mt-4 flex items-center gap-4">
          <Donut percent={stats.percent} />
          <div className="space-y-1 text-sm">
            <div>
              <div className="text-ink-muted">Уроки</div>
              <div className="font-semibold text-ink">
                {stats.lessonsDone} из {stats.lessonsTotal}
              </div>
            </div>
            <div>
              <div className="text-ink-muted">Курсы</div>
              <div className="font-semibold text-ink">
                {stats.coursesDone} из {stats.coursesTotal}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Per-course progress */}
      <section className="card p-5 lg:col-span-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Текущие курсы
        </div>
        {courses === null ? (
          <div className="mt-3 space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-surface-alt" />
            ))}
          </div>
        ) : courses.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">Курсов не назначено.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {courses.map((c) => (
              <li key={c.course_id}>
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate font-medium text-ink">
                    {c.course_title}
                  </span>
                  <span className="ml-2 shrink-0 text-xs font-semibold tabular-nums text-ink">
                    {c.percent}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
                  <div
                    className={`h-full transition-all ${
                      c.percent === 100 ? "bg-brand-success" : "bg-brand"
                    }`}
                    style={{ width: `${c.percent}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Status panel */}
      <section className="card p-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Статус обучения
        </div>
        <ul className="mt-3 space-y-2 text-sm">
          <StatusRow
            label="Теория"
            value={
              stats.lessonsTotal === 0
                ? "—"
                : stats.lessonsDone === stats.lessonsTotal
                  ? "Пройдено"
                  : `${stats.lessonsDone}/${stats.lessonsTotal}`
            }
            tone={
              stats.lessonsTotal > 0 && stats.lessonsDone === stats.lessonsTotal
                ? "success"
                : "neutral"
            }
          />
          <StatusRow
            label="Тесты"
            value={
              stats.coursesDone === stats.coursesTotal && stats.coursesTotal > 0
                ? "Пройдено"
                : "В процессе"
            }
            tone={
              stats.coursesDone === stats.coursesTotal && stats.coursesTotal > 0
                ? "success"
                : "neutral"
            }
          />
          <StatusRow label="Практика в песочнице" value="Скоро" tone="muted" />
          <StatusRow label="Полевая практика" value="Скоро" tone="muted" />
          <StatusRow label="Спич" value="Скоро" tone="muted" />
        </ul>
      </section>
    </div>
  );
}

function CoursesTab({ courses }: { courses: MyCourseSummary[] | null }) {
  if (courses === null) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-surface-alt" />
        ))}
      </div>
    );
  }
  if (courses.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-muted">
        Курсов не назначено.
      </div>
    );
  }
  return (
    <ul className="space-y-3">
      {courses.map((c) => (
        <li key={c.course_id} className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-ink">{c.course_title}</div>
              <div className="mt-0.5 text-xs text-ink-muted">
                {c.is_mandatory ? "Базовый" : "Маршрут"}
                {c.due_at && (
                  <> · до {formatMoscowDate(c.due_at)}</>
                )}
                {c.overdue && (
                  <span className="badge badge-danger ml-2">Просрочен</span>
                )}
              </div>
            </div>
            <div className="shrink-0 text-sm font-semibold tabular-nums text-ink">
              {c.percent}%
            </div>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-alt">
            <div
              className={`h-full transition-all ${
                c.percent === 100 ? "bg-brand-success" : "bg-brand"
              }`}
              style={{ width: `${c.percent}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-ink-muted">
            {c.lessons_completed} из {c.lessons_total} уроков
          </div>
        </li>
      ))}
    </ul>
  );
}

function PracticeTab({
  userId,
  canManage,
}: {
  userId: number;
  canManage: boolean;
}) {
  const [items, setItems] = useState<FieldObservationOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [completeId, setCompleteId] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  async function load() {
    try {
      const r = await listForUser(userId);
      setItems(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось");
      setItems([]);
    }
  }

  useEffect(() => {
    void load();
  }, [userId]);

  async function handleAdd() {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await createObservation({
        trainee_user_id: userId,
        title: title.trim(),
        scheduled_at: moscowDateTimeToUtcIso(scheduledAt),
      });
      setTitle("");
      setScheduledAt("");
      setAddOpen(false);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  async function handleComplete() {
    if (completeId == null) return;
    setBusy(true);
    try {
      await completeObservation(completeId, comment.trim() || undefined);
      setCompleteId(null);
      setComment("");
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  if (items === null) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-alt" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {canManage && (
        <div className="mb-4">
          {addOpen ? (
            <div className="card flex flex-wrap gap-2 p-3">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Маршрут / название наблюдательной экскурсии"
                className="input min-w-[260px] flex-1"
              />
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="input w-56"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={busy || !title.trim()}
                className="btn-primary"
              >
                Запланировать
              </button>
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                disabled={busy}
                className="btn-secondary"
              >
                Отмена
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              <Plus size={14} strokeWidth={2.25} />
              Запланировать экскурсию
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="card p-12 text-center">
          <MapPin size={32} strokeWidth={1.5} className="mx-auto text-ink-muted/50" />
          <p className="mt-3 text-sm text-ink-muted">
            Наблюдательных экскурсий пока нет.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((o, idx) => (
            <li
              key={o.id}
              className="card flex items-start gap-3 p-4"
            >
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
                  o.status === "completed"
                    ? "bg-brand-success text-white"
                    : o.status === "cancelled"
                      ? "bg-surface-alt text-ink-muted"
                      : "bg-brand/10 text-brand"
                }`}
              >
                {o.status === "completed" ? (
                  <Check size={14} strokeWidth={2.5} />
                ) : o.status === "cancelled" ? (
                  <XCircle size={14} strokeWidth={2} />
                ) : (
                  <span className="text-[11px] font-semibold">{idx + 1}</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-ink">{o.title}</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {o.status === "completed"
                    ? `Пройдено ${o.completed_at ? formatMoscowDate(o.completed_at) : ""}`
                    : o.status === "cancelled"
                      ? "Отменено"
                      : o.scheduled_at
                        ? `Запланировано: ${formatMoscowDateTime(o.scheduled_at, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}`
                        : "Без даты"}
                </div>
                {o.mentor_comment && (
                  <p className="mt-2 rounded bg-surface-alt/60 px-2 py-1.5 text-xs italic text-ink-soft">
                    «{o.mentor_comment}»
                  </p>
                )}
              </div>
              {canManage && o.status === "scheduled" && (
                <button
                  type="button"
                  onClick={() => {
                    setCompleteId(o.id);
                    setComment("");
                  }}
                  className="btn-secondary shrink-0 text-xs"
                >
                  Отметить пройденной
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {completeId !== null && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/50 p-4">
          <div className="card w-full max-w-md p-5">
            <h3 className="text-base font-semibold text-ink">
              Комментарий куратора
            </h3>
            <p className="mt-0.5 text-xs text-ink-muted">
              Появится у ученика рядом с экскурсией. Можно оставить пустым.
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="input mt-3 w-full"
              placeholder="Что прошло хорошо, что можно подтянуть…"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCompleteId(null)}
                disabled={busy}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleComplete}
                disabled={busy}
                className="btn-primary"
              >
                Отметить пройденной
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExamsTab({
  userId,
  userName,
  canManage,
}: {
  userId: number;
  userName: string;
  canManage: boolean;
}) {
  const [items, setItems] = useState<ExamScheduleOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await listExamsForUser(userId);
      setItems(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось");
      setItems([]);
    }
  }

  useEffect(() => {
    void load();
  }, [userId]);

  async function handleCancel(id: number) {
    if (!confirm("Отменить этот экзамен?")) return;
    setBusy(true);
    try {
      await cancelExam(id);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  async function handleComplete(id: number) {
    if (!confirm("Отметить экзамен пройденным? (это запись для админ-журнала; для записи результата экзамена используйте «Допустить к работе»)")) return;
    setBusy(true);
    try {
      await completeExam(id);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Не удалось");
    } finally {
      setBusy(false);
    }
  }

  if (items === null) {
    return (
      <div className="space-y-2">
        {[0, 1].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl bg-surface-alt" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {canManage && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="btn-primary inline-flex items-center gap-1.5"
          >
            <CalendarPlus size={14} strokeWidth={2.25} />
            Назначить экзамен
          </button>
        </div>
      )}

      {error && (
        <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="card p-12 text-center">
          <CalendarPlus
            size={32}
            strokeWidth={1.5}
            className="mx-auto text-ink-muted/50"
          />
          <p className="mt-3 text-sm text-ink-muted">
            Экзамены ещё не назначены.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((e) => (
            <li key={e.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-ink">
                      {formatMoscowDateTime(e.scheduled_at, {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="badge badge-neutral">
                      {e.format === "in_person" ? "Очный" : "Онлайн"}
                    </span>
                    <span
                      className={`badge ${
                        e.status === "completed"
                          ? "badge-success"
                          : e.status === "cancelled"
                            ? "badge-danger"
                            : "badge-brand"
                      }`}
                    >
                      {e.status === "completed"
                        ? "Проведён"
                        : e.status === "cancelled"
                          ? "Отменён"
                          : "Запланирован"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    Курс: <span className="text-ink">#{e.course_id}</span>
                    {e.examiner_user_id && (
                      <> · экзаменатор #{e.examiner_user_id}</>
                    )}
                  </div>
                  {e.comment && (
                    <p className="mt-2 rounded bg-surface-alt/60 px-2 py-1.5 text-xs italic text-ink-soft">
                      «{e.comment}»
                    </p>
                  )}
                </div>
                {canManage && e.status === "scheduled" && (
                  <div className="flex shrink-0 flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => handleComplete(e.id)}
                      disabled={busy}
                      className="btn-secondary text-xs"
                    >
                      Провёден
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancel(e.id)}
                      disabled={busy}
                      className="btn-danger text-xs"
                    >
                      Отменить
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialogOpen && (
        <ScheduleExamDialog
          traineeUserId={userId}
          traineeName={userName}
          onClose={() => setDialogOpen(false)}
          onScheduled={() => {
            setDialogOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

function ComingSoonTab() {
  return (
    <div className="card p-12 text-center">
      <ShieldOff
        size={36}
        strokeWidth={1.5}
        className="mx-auto text-ink-muted/50"
      />
      <p className="mt-3 text-sm text-ink-muted">
        Раздел появится в следующей итерации.
      </p>
    </div>
  );
}

function Donut({ percent }: { percent: number }) {
  const radius = 36;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - percent / 100);
  return (
    <svg width={96} height={96} viewBox="0 0 96 96" className="shrink-0">
      <circle
        cx={48}
        cy={48}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={8}
        className="text-surface-alt"
      />
      <circle
        cx={48}
        cy={48}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={8}
        strokeLinecap="round"
        className={percent === 100 ? "text-brand-success" : "text-brand"}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 48 48)"
      />
      <text
        x={48}
        y={52}
        textAnchor="middle"
        className="fill-ink text-base font-bold tabular-nums"
      >
        {percent}%
      </text>
    </svg>
  );
}

function StatusRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "success" | "neutral" | "muted";
}) {
  const valueClass =
    tone === "success"
      ? "badge-success"
      : tone === "muted"
        ? "badge-neutral"
        : "badge-brand";
  return (
    <li className="flex items-center justify-between">
      <span className="text-ink">{label}</span>
      <span className={`badge ${valueClass}`}>{value}</span>
    </li>
  );
}
